import {
  EXTRACTION_PRESETS,
  EXTRACTION_RANGES,
  extractionSettingsEqual,
} from "../../audio/extractionStorage";
import type { ExtractionSettings } from "../../audio/features";
import { FaderRow } from "./FaderRow";

interface Props {
  settings: ExtractionSettings;
  onChange: (settings: ExtractionSettings) => void;
}

/** Slider rows for the live extraction settings: key, label, readout format. */
const ROWS: Array<{ key: keyof ExtractionSettings; label: string; format: (v: number) => string }> = [
  { key: "minSnr", label: "Sensitivity (min S/N)", format: (v) => `${v.toFixed(1)}σ` },
  { key: "minBaseWidthA", label: "Min width", format: (v) => `${v.toFixed(0)} Å` },
  { key: "maxBaseWidthA", label: "Max width", format: (v) => `${v.toFixed(0)} Å` },
  { key: "edgeMarginA", label: "Edge margin", format: (v) => `${v.toFixed(0)} Å` },
  { key: "knownMatchToleranceA", label: "Line match tol.", format: (v) => `${v.toFixed(0)} Å` },
  { key: "maxDiscoveryFeatures", label: "Discovery cap", format: (v) => v.toFixed(0) },
];

export function ExtractionSection({ settings, onChange }: Props) {
  return (
    <div className="panel-section">
      <h2>Feature detection</h2>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
        {EXTRACTION_PRESETS.map((preset) => (
          <button
            key={preset.id}
            className={extractionSettingsEqual(settings, preset.settings) ? "active" : ""}
            onClick={() => onChange({ ...preset.settings })}
          >
            {preset.label}
          </button>
        ))}
      </div>
      {ROWS.map(({ key, label, format }) => (
        <FaderRow
          key={key}
          label={label}
          min={EXTRACTION_RANGES[key].min}
          max={EXTRACTION_RANGES[key].max}
          step={EXTRACTION_RANGES[key].step}
          value={settings[key]}
          onChange={(v) => onChange({ ...settings, [key]: v })}
          readout={format(settings[key])}
        />
      ))}
      <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>
        Live thresholds for the peak detector: what counts as a feature,
        how close to the coverage edges to look, and how far a peak may sit
        from a known line and still take its name.
      </p>
    </div>
  );
}
